<script setup lang="ts">
import { mdiLanguagePython } from '@mdi/js'

interface EntryPointFile {
  path: string
  size?: number
}

const props = defineProps<{
  modelValue: boolean
  title?: string
  subtitle?: string
  files: EntryPointFile[]
  loading?: boolean
  /**
   * #764: 取り込まれる合計サイズ。GitHub 経路では**ここが取り込み前の最後の分岐点**
   * なので、重い zipball を落とす前に大きさを見せる（Trees API が返す blob の
   * size 合計なので追加のダウンロードは無い）。
   */
  totalSizeBytes?: number | null
  /** Trees API が結果を打ち切った場合。合計は下限なので断定しない言い方にする。 */
  sizeTruncated?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select': [path: string]
  'cancel': []
}>()

const selectedFile = ref<string[] | null>(null)

// #764: 取り込み前に見せる合計サイズ。渡されなければ行ごと出さない。
const totalSizeLabel = computed(() => sizeLabel(props.totalSizeBytes, props.sizeTruncated))

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

watch(() => props.modelValue, (open) => {
  if (open) selectedFile.value = null
})

const onSelect = () => {
  const selected = Array.isArray(selectedFile.value) ? selectedFile.value[0] : selectedFile.value
  if (selected) {
    emit('select', selected)
  }
}

const onCancel = () => {
  emit('cancel')
}
</script>

<template>
  <v-dialog
    v-model="visible"
    max-width="500"
    persistent
  >
    <v-card class="entry-point-card">
      <v-card-title class="entry-point-title">
        {{ title || 'エントリーポイント' }}
      </v-card-title>
      <v-card-subtitle class="entry-point-subtitle">
        {{ subtitle || 'エントリーポイントとなる .py ファイルを選択してください' }}
      </v-card-subtitle>
      <v-card-subtitle
        v-if="totalSizeLabel"
        class="entry-point-size"
      >
        この作品の大きさ： <strong>{{ totalSizeLabel }}</strong>
      </v-card-subtitle>

      <v-card-text>
        <div
          v-if="loading"
          class="text-center pa-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
          <p class="mt-2">
            ファイルを読み込み中...
          </p>
        </div>

        <div
          v-else-if="files.length === 0"
          class="text-center pa-4 text-grey"
        >
          .py ファイルが見つかりません
        </div>

        <v-list
          v-else
          v-model:selected="selectedFile"
          select-strategy="single-leaf"
          density="compact"
          class="entry-point-list"
        >
          <v-list-item
            v-for="file in files"
            :key="file.path"
            :value="file.path"
          >
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-radio
                  :model-value="isActive"
                  color="primary"
                />
              </v-list-item-action>
            </template>
            <v-list-item-title>
              <v-icon
                :icon="mdiLanguagePython"
                size="small"
                class="mr-1"
              />{{ file.path }}
            </v-list-item-title>
            <template #append>
              <span
                v-if="file.size"
                class="text-caption text-grey"
              >{{ (file.size / 1024).toFixed(1) }}KB</span>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="onCancel"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="!selectedFile || selectedFile.length === 0"
          @click="onSelect"
        >
          選択
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.entry-point-card {
  font-family: "Noto Sans JP", sans-serif;
}

.entry-point-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.entry-point-subtitle {
  font-size: 0.85rem;
  opacity: 0.8;
}

.entry-point-size {
  padding-top: 0;
  font-size: 0.85rem;
  opacity: 0.9;
}

.entry-point-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
